import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Desktop header menu controlled by deliberate clicks instead of hover.
 * Mobile navigation is rendered by a separate drawer component and is unaffected.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 * @property {HTMLElement | null} activeTrigger
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {State} */
  #state = {
    activeItem: null,
    activeTrigger: null,
  };

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {MutationObserver | null} */
  #triggerMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    this.#prepareTriggers();

    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('keydown', this.#documentKeydownListener);
    document.addEventListener('pointerdown', this.#outsidePointerListener, true);
    document.addEventListener('focusin', this.#outsideFocusListener, true);
    this.addEventListener('click', this.#clickListener);
    this.addEventListener('keydown', this.#menuKeydownListener);

    this.#triggerMutationObserver = new MutationObserver(this.#prepareTriggers);
    this.#triggerMutationObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('keydown', this.#documentKeydownListener);
    document.removeEventListener('pointerdown', this.#outsidePointerListener, true);
    document.removeEventListener('focusin', this.#outsideFocusListener, true);
    this.removeEventListener('click', this.#clickListener);
    this.removeEventListener('keydown', this.#menuKeydownListener);

    this.#triggerMutationObserver?.disconnect();
    this.#triggerMutationObserver = null;
    this.#cleanupMutationObserver();
  }

  /**
   * Keep the menu mode in sync with the header and close a desktop menu that
   * is no longer being rendered as a desktop menu.
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    requestAnimationFrame(() => {
      if (this.headerComponent?.dataset.menuStyle !== 'menu') {
        this.#forceResetMenu();
      }
    });
  }, 100);

  /**
   * Make parent links behave as accessible disclosure buttons without changing
   * simple navigation links that do not own a submenu.
   */
  #prepareTriggers = () => {
    this.querySelectorAll('[ref="menuitem"][aria-haspopup="true"]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement)) return;
      trigger.setAttribute('role', 'button');
      trigger.dataset.ddMenuTrigger = 'true';
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
    });

    const moreTrigger = this.querySelector('[slot="more"] > .menu-list__link');
    if (moreTrigger instanceof HTMLElement) {
      moreTrigger.setAttribute('aria-haspopup', 'true');
      moreTrigger.setAttribute('aria-expanded', moreTrigger.getAttribute('aria-expanded') || 'false');
      moreTrigger.dataset.ddMenuTrigger = 'true';
    }
  };

  /**
   * Preserve the declarative methods referenced by the Liquid markup. Hover and
   * focus events intentionally do nothing in click mode.
   * @param {Event} event
   */
  activate = (event) => {
    if (event.type === 'click') this.toggle(event);
  };

  /** Click mode never closes from pointerleave or blur. */
  deactivate() {}

  /**
   * Public toggle method for deliberate activation.
   * @param {Event} event
   */
  toggle = (event) => {
    const trigger = this.#findTrigger(event.target);
    if (!trigger) return;

    const item = this.#findMenuItem(trigger);
    if (!item) return;

    if (item === this.#state.activeItem) {
      this.#forceResetMenu();
      return;
    }

    this.#openMenu(item, trigger);
  };

  /** @param {MouseEvent} event */
  #clickListener = (event) => {
    const trigger = this.#findTrigger(event.target);
    if (!trigger) return;

    const item = this.#findMenuItem(trigger);
    if (!item) return;

    event.preventDefault();
    event.stopPropagation();
    this.toggle(event);
  };

  /** @param {KeyboardEvent} event */
  #menuKeydownListener = (event) => {
    const trigger = this.#findTrigger(event.target);
    if (!trigger) return;

    if (event.key === ' ') {
      event.preventDefault();
      this.toggle(event);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      const item = this.#findMenuItem(trigger);
      if (!item) return;
      if (item !== this.#state.activeItem) this.#openMenu(item, trigger);

      requestAnimationFrame(() => {
        const submenu = this.#findSubmenu(item);
        submenu?.querySelector('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')?.focus();
      });
    }
  };

  /** @param {KeyboardEvent} event */
  #documentKeydownListener = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    const trigger = this.#state.activeTrigger;
    this.#forceResetMenu();
    trigger?.focus();
  };

  /** @param {PointerEvent} event */
  #outsidePointerListener = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;
    this.#forceResetMenu();
  };

  /** @param {FocusEvent} event */
  #outsideFocusListener = (event) => {
    if (!this.#state.activeItem) return;
    if (event.composedPath().includes(this)) return;
    this.#forceResetMenu();
  };

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #findTrigger(target) {
    if (!(target instanceof Element)) return null;

    const trigger = target.closest('[data-dd-menu-trigger="true"]');
    return trigger instanceof HTMLElement && this.contains(trigger) ? trigger : null;
  }

  /**
   * @param {HTMLElement} trigger
   * @returns {HTMLElement | null}
   */
  #findMenuItem(trigger) {
    const listItem = trigger.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return null;

    if (listItem.matches('[slot="more"]')) {
      const firstOverflowItem = this.querySelector('[slot="overflow"] [ref="menuitem"]');
      return firstOverflowItem instanceof HTMLElement ? firstOverflowItem : null;
    }

    const item = listItem.querySelector(':scope > [ref="menuitem"]');
    return item instanceof HTMLElement ? item : null;
  }

  /**
   * @param {HTMLElement | null} item
   * @returns {HTMLElement | null}
   */
  #findSubmenu(item) {
    const submenu = item?.parentElement?.querySelector(':scope > [ref="submenu[]"]');
    return submenu instanceof HTMLElement ? submenu : null;
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement} trigger
   */
  #openMenu(item, trigger) {
    if (!this.headerComponent || this.headerComponent.dataset.menuStyle !== 'menu') return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = this.#findSubmenu(previouslyActiveItem);
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    const moreTrigger = this.querySelector('[slot="more"] > .menu-list__link');
    if (moreTrigger instanceof HTMLElement) moreTrigger.ariaExpanded = 'false';

    const listItem = item.closest('.menu-list__list-item');
    const isOverflowItem = listItem instanceof HTMLElement && listItem.slot === 'overflow';

    this.#state.activeItem = item;
    this.#state.activeTrigger = trigger;
    this.ariaExpanded = 'true';
    this.dataset.overflowExpanded = isOverflowItem.toString();
    item.ariaExpanded = 'true';
    trigger.ariaExpanded = 'true';

    let submenu = this.#findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && isOverflowItem) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';
      this.#watchSubmenuHeight(submenu);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    if (isOverflowItem) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        finalHeight = Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) finalHeight = 0;

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /** @param {HTMLElement} submenu */
  #watchSubmenuHeight(submenu) {
    this.#cleanupMutationObserver();

    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (submenu.offsetHeight > 0) {
            this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
            this.#setFullOpenHeaderHeight(submenu.offsetHeight);
            this.#cleanupMutationObserver();
          }
        });
      });
    });

    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });
    setTimeout(() => this.#cleanupMutationObserver(), 500);
  }

  #forceResetMenu() {
    if (!this.#state.activeItem && this.ariaExpanded !== 'true') return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.ariaExpanded = 'false';

    this.querySelectorAll('[ref="menuitem"], [slot="more"] > .menu-list__link').forEach((item) => {
      if (item instanceof HTMLElement) item.ariaExpanded = 'false';
    });

    this.querySelectorAll('[ref="submenu[]"], .menu-list__submenu').forEach((submenu) => {
      if (submenu instanceof HTMLElement) delete submenu.dataset.active;
    });

    this.#state.activeItem = null;
    this.#state.activeTrigger = null;
    this.#cleanupMutationObserver();
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /** @param {(submenu: HTMLElement) => void} callback */
    const mapSubmenus = (callback) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = link.querySelector('[ref="submenu[]"]');
        if (submenu instanceof HTMLElement) callback(submenu);
      });
    };

    mapSubmenus((submenu) => submenu.style.setProperty('display', 'none'));
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => submenu.style.removeProperty('display'));
    return height;
  }

  /** @param {number} submenuHeight */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? this.headerComponent.querySelector('.header__row--top')?.offsetHeight || 0
        : this.headerComponent.offsetHeight;

    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + headerVisibleHeight;
    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]')
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  #preloadImages = () => {
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
